<template>
  <app-page
    :title="employee ? $t('employee.pause.title', { name: employee.full_name }) : $t('employee.pause.titleFallback')"
    :error="error"
    :previous="[
      { label: $t('employee.previousLabel'), to: { name: 'employees' } },
      {
        label: employee ? employee.full_name : $t('employee.title'),
        to: { name: 'employee', params: { employeeId: $route.params.employeeId } },
      },
    ]"
    icon="pause"
  >
    <app-loading :loading="loading" />
    <template v-if="!loading && !saved">
      <sme-alert level="warning" class="mb-3">
        {{ $t('employee.pause.warningAlert', { name: employee.full_name }) }}
      </sme-alert>

      <p class="text-muted">
        <i18n path="employee.pause.retainAccessBody" tag="span">
          <template #name>{{ employee.full_name }}</template>
          <template #pausedBold
            ><strong>{{ $t('employee.pause.pausedBold') }}</strong></template
          >
        </i18n>
        <template v-if="showSalaryAccrualOption"> {{ $t('employee.pause.salaryAccrualOptionalBody') }}</template>
      </p>

      <ValidationObserver v-slot="v">
        <b-form @submit.prevent="v.handleSubmit(onSubmit)">
          <app-input
            v-model="reason"
            type="text"
            name="reason"
            rules="required"
            :label="$t('employee.pause.reasonLabel')"
            :disabled="saving || saved"
            :validate-immediate="false"
          />
          <app-input
            v-model="message"
            type="select"
            name="message"
            rules="required"
            :label="$t('employee.pause.messageLabel')"
            :disabled="saving || saved"
            :options="messageOptions"
            :validate-immediate="false"
          />
          <app-input
            v-if="message === OTHER_MESSAGE"
            v-model="messageOther"
            type="text"
            name="messageOther"
            :placeholder="$t('employee.pause.messageOtherPlaceholder')"
            :rules="{ required: message === OTHER_MESSAGE }"
            :disabled="saving || saved"
            :validate-immediate="false"
          />
          <sme-alert level="info" class="mb-3" v-if="isEnableEmployeePauseScheduling">
            {{ $t('employee.pause.pauseDateOptionalAlert') }}
          </sme-alert>
          <app-input
            v-if="isEnableEmployeePauseScheduling"
            v-model="pausedAtDate"
            type="date"
            name="pause-at"
            :label="$t('employee.pause.pausedAtLabel')"
            :min="pauseAtDateMin"
            :max="expiresAtDate"
            isNullable
          />
          <sme-alert level="info" class="mb-3">
            {{ $t('employee.pause.unpauseDateOptionalAlert') }}
          </sme-alert>
          <app-input
            v-model="expiresAtDate"
            type="date"
            name="unpause-at"
            :label="$t('employee.pause.expiresAtLabel')"
            :min="pausedAtDate ? pausedAtDate : expiresAtDateMin"
            isNullable
          />
          <app-input
            v-if="showSalaryAccrualOption"
            v-model="pauseSalaryAccrual"
            type="select"
            name="Stop accrual"
            rules="required"
            :label="$t('employee.pause.stopAccrualLabel')"
            :options="salaryAccrualOptions"
          />
          <page-sub-footer>
            <b-button
              data-testid="pause-button"
              type="submit"
              variant="primary"
              :disabled="v.invalid || saving || saved || !checkDatesAreValid()"
            >
              <b-spinner v-if="saving" class="mr-2" small />
              {{ saving ? $t('employee.pause.submittingButton') : $t('employees.pauseFlexiblePayLabel') }}
            </b-button>
          </page-sub-footer>
        </b-form>
      </ValidationObserver>
    </template>
    <sme-alert v-if="saved" level="good">
      {{ $t('employee.pause.successAlert', { name: employee.full_name }) }}
    </sme-alert>
  </app-page>
</template>

<script>
import { extend, ValidationObserver } from 'vee-validate';
import ApiClient from '@/ApiClient';
import AppInput from '@/components/AppInput.vue';
import AppLoading from '@/components/AppLoading.vue';
import AppPage from '@/components/AppPage.vue';
import PageSubFooter from '@/components/PageSubFooter.vue';
import SmeAlert from '@/components/atoms/SmeAlert.vue';
import State from '@/state/State';
import { EMPLOYEE_PAY_TYPE } from '@/utils/Employee';
import useFeatureFlags from '@/composables/useFeatureFlags';
import moment from 'moment';

extend('date_validate', {
  validate(time) {
    return time !== null;
  },
  params: ['time'],
});

const OTHER_MESSAGE = 'Other (please specify)';
const { isEnableEmployeePauseScheduling } = useFeatureFlags();

export default {
  name: 'EmployeePause',
  components: { ValidationObserver, AppInput, AppLoading, AppPage, PageSubFooter, SmeAlert },
  setup() {
    return {
      isEnableEmployeePauseScheduling,
    };
  },
  data() {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return {
      loading: true,
      saving: false,
      saved: false,
      error: undefined,
      employee: undefined,
      reason: undefined,
      pauseSalaryAccrual: false,
      expiresAtDate: null,
      pausedAtDate: null,
      expiresAtDateMin: tomorrow,
      pauseAtDateMin: tomorrow,
      language_code: State.state.language_code.slice(0, 2),
      OTHER_MESSAGE: OTHER_MESSAGE,
    };
  },
  methods: {
    onSubmit() {
      this.saving = true;
      const newEmployee = { ...this.employee };

      if (!newEmployee.properties) {
        newEmployee.properties = {};
      }

      // If there is no scheduled pause date, pause immediately
      newEmployee.properties.paused = !this.pausedAtDate;
      newEmployee.properties.paused_timestamp = !this.pausedAtDate ? new Date().toISOString() : null;
      newEmployee.properties.paused_message = this.message === OTHER_MESSAGE ? this.messageOther : this.message;
      newEmployee.properties.paused_by_user_id = State.state.user?.user_id ?? null;
      newEmployee.properties.paused_reason = this.reason;
      newEmployee.properties.pause_expires_at = !this.expiresAtDate ? null : this.expiresAtDate;
      newEmployee.properties.pause_scheduled_at = !this.pausedAtDate ? null : this.pausedAtDate;
      // isEnableEmployeePauseScheduling on for US only, if there is no pausedAtDate, pause salary accrual
      newEmployee.properties.pause_salary_accrual =
        isEnableEmployeePauseScheduling.value && !this.pausedAtDate ? true : this.pauseSalaryAccrual;

      ApiClient.updateEmployee(newEmployee).then(
        () => {
          this.saving = false;
          this.saved = true;
        },
        error => {
          this.error = error.message;
          this.loading = false;
        },
      );
    },
    checkDatesAreValid() {
      if (!this.pausedAtDate || !this.expiresAtDate) {
        return true;
      }
      return (
        this.pausedAtDate && this.expiresAtDate && moment(this.pausedAtDate).isBefore(moment(this.expiresAtDate), 'day')
      );
    },
    loadData() {
      this.loading = true;
      ApiClient.getEmployee(this.$route.params.employeeId).then(
        employee => {
          this.employee = employee;
          this.loading = false;
        },
        error => {
          this.error = error.message;
          this.loading = false;
        },
      );
    },
  },
  mounted() {
    this.loadData();
  },
  computed: {
    employeeSalaried() {
      return this.employee.salary_properties.salary_or_hourly === EMPLOYEE_PAY_TYPE.SALARY;
    },
    showSalaryAccrualOption() {
      return this.employeeSalaried && !this.isEnableEmployeePauseScheduling;
    },
    salaryAccrualOptions() {
      return [
        { text: this.$t('employees.filters.yesOption'), value: true },
        { text: this.$t('employees.filters.noOption'), value: false },
      ];
    },
    messageOptions() {
      return [
        { text: this.$t('employee.pause.messageOptions.holiday'), value: 'Holiday' },
        { text: this.$t('employee.pause.messageOptions.absence'), value: 'Absence' },
        { text: this.$t('employee.pause.messageOptions.maternityLeave'), value: 'Maternity leave' },
        { text: this.$t('employee.pause.messageOptions.sickLeave'), value: 'Sick leave' },
        { text: this.$t('employee.pause.messageOptions.other'), value: OTHER_MESSAGE },
      ];
    },
  },
};
</script>

<style>
#pause-expiration-datepicker-label {
  font-weight: 500 !important;
}
</style>
